import type { PrivacyRetentionMatrix } from "@lafayette/schemas";
import { type ReactNode, useState } from "react";

import { CommandDialog } from "../../ui/command-dialog";
import { CheckboxField } from "../../ui/fields";
import { StatusMessage } from "../../ui/status-message";
import { describePrivacyError } from "../errors";
import { actionLabels, actionOrder, stepLabels } from "../labels";

// The confirm dialog of every privacy command (CHT-771), an adapter on the generic `CommandDialog` of the kit. It shows EXACTLY what the
// command does, in plain words:
//   - for a deletion: what is deleted, what is anonymized, what is deleted at a provider and what is kept and why (the retention matrix of the
//     server, the same data the deletion follows), the steps in order, and that it cannot be undone;
//   - for an export: what the file holds, that it is a file for the customer's own data, that it lives 24 hours, and that it can be downloaded five times;
//   - for a rejection, a cancel or a retry: one sentence.
// A deletion needs a tick in a box before the button works (`confirmDisabled`). A failure is shown in the dialog; a request that changed while
// the person looked (409) closes the way to retry, and the page behind reads again.

type PrivacyDialogSubject = "DELETE" | "EXPORT" | "OTHER";

type Props = Readonly<{
  title: string;
  confirmLabel: string;
  tone?: "primary" | "danger";
  subject: PrivacyDialogSubject;
  /** One plain sentence for the top of the dialog. */
  summary: ReactNode;
  /** The matrix of the server. A deletion needs it; without it the dialog says it could not be loaded and offers no confirm. */
  matrix?: PrivacyRetentionMatrix | undefined;
  /** Extra fields of the command (customer id, kind, reason). */
  children?: ReactNode;
  /** `false`: the fields are not valid; the dialog does not send. */
  canSubmit?: boolean;
  send: () => Promise<unknown>;
  onDone: () => void;
  onReload: () => void;
  onClose: () => void;
}>;

function MatrixSummary({ matrix }: Readonly<{ matrix: PrivacyRetentionMatrix }>) {
  return (
    // A box that scrolls must be reachable by keyboard (WCAG 2.1.1), and a region has a name.
    <section
      aria-label="What each action keeps or removes"
      // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be reachable by keyboard
      tabIndex={0}
      className="grid max-h-[46vh] gap-2 overflow-auto rounded-md border border-border px-3 py-2"
    >
      {actionOrder.map((action) => {
        const rows = matrix.rows.filter((row) => row.action === action);
        if (rows.length === 0) return null;
        return (
          <section key={action} aria-label={actionLabels[action]}>
            <h3 className="m-0 mt-2 mb-1 text-sm font-semibold">{actionLabels[action]}</h3>
            <ul className="m-0 list-disc pl-5 text-sm leading-6">
              {rows.map((row) => (
                <li key={row.dataClass}>
                  <strong>{row.label}</strong>
                  <span className="text-muted-foreground"> {row.why}</span>
                  {row.retention === null ? null : (
                    <span className="text-muted-foreground"> How long: {row.retention}</span>
                  )}
                  {row.legalOpen ? (
                    <span className="font-semibold">
                      {" "}
                      This period is not confirmed by law advice yet.
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      <section aria-label="Steps, in order">
        <h3 className="m-0 mt-2 mb-1 text-sm font-semibold">The steps, in order</h3>
        <ol className="m-0 list-decimal pl-5 text-sm leading-6">
          {matrix.steps.map((entry) => (
            <li key={entry.step}>
              {stepLabels[entry.step]}
              {entry.phase === "provider" ? " (calls another provider)" : ""}
            </li>
          ))}
        </ol>
      </section>
    </section>
  );
}

function ExportSummary({ matrix }: Readonly<{ matrix: PrivacyRetentionMatrix | undefined }>) {
  return (
    <ul className="m-0 grid list-disc gap-1.5 pl-5 text-sm leading-6">
      <li>
        The file holds only what the customer can already see in the app: profile, settings,
        memories with where they came from, trips, saved items, requests, payments and the metadata
        of the conversation.
      </li>
      <li>It holds no staff note, no internal evidence and no data of another customer.</li>
      <li>
        The file is kept for {matrix?.limits.exportTtlHours ?? 24} hours and can be downloaded{" "}
        {matrix?.limits.exportMaxDownloads ?? 5} times. Each download is written in the audit trail.
      </li>
      <li>
        A customer can ask for {matrix?.limits.exportDailyLimit ?? 3} exports in 24 hours. Another
        supervisor or administrator must approve a request that staff make.
      </li>
    </ul>
  );
}

export function PrivacyConfirmDialog({
  title,
  confirmLabel,
  tone = "primary",
  subject,
  summary,
  matrix,
  children,
  canSubmit = true,
  send,
  onDone,
  onReload,
  onClose,
}: Props) {
  const [understood, setUnderstood] = useState(false);

  const matrixMissing = subject === "DELETE" && matrix === undefined;
  const needsTick = subject === "DELETE" && !understood;
  const blocked = needsTick || matrixMissing || !canSubmit;

  return (
    <CommandDialog<void>
      title={title}
      description={summary}
      confirmLabel={confirmLabel}
      // A failed send that may work again says the same words as before: the person confirms the same thing once more.
      retryLabel={confirmLabel}
      tone={tone}
      confirmDisabled={blocked}
      confirmDisabledReason={
        needsTick && !matrixMissing ? "Tick the box to confirm that you have read this." : undefined
      }
      mutate={async () => {
        try {
          await send();
          return { ok: true, value: undefined };
        } catch (error) {
          const described = describePrivacyError(error);
          return {
            ok: false,
            failure: {
              message: described.message,
              retryable: described.retryable,
              keepForm: described.kind === "bad_request",
              reload: described.reload,
              tone: described.kind === "conflict" ? "conflict" : "error",
              kind: described.kind,
            },
          };
        }
      }}
      onFailureReload={onReload}
      onDone={onDone}
      onClose={onClose}
    >
      {children}
      {subject === "DELETE" ? (
        matrix === undefined ? (
          <StatusMessage
            message={{
              tone: "error",
              text: "What a deletion does could not be loaded. Close this window and try again.",
            }}
          />
        ) : (
          <>
            <MatrixSummary matrix={matrix} />
            <p
              className="m-0 rounded-sm border border-destructive-border bg-destructive-soft px-3.5 py-2.5 text-sm leading-6 font-semibold text-destructive"
              role="note"
            >
              This cannot be undone. The customer's sign-in account is deleted and the customer
              cannot use Lafayette again with it.
            </p>
            <CheckboxField
              id="privacy-understood"
              label="I have read what will happen and I understand that it cannot be undone."
              checked={understood}
              onChange={setUnderstood}
            />
          </>
        )
      ) : null}
      {subject === "EXPORT" ? <ExportSummary matrix={matrix} /> : null}
    </CommandDialog>
  );
}
